import {getIn, setIn} from 'formik';

/**
 * TOUR-03 lot 3 — règles pures de l'écran Simulation (ecran-simulation.md §3, U-1 à U-8).
 * Aucune dépendance React : tout ce qui se teste vit ici, les composants ne font que l'afficher.
 * Les noms de champs viennent de l'API (SimulationScenarios, SimulationJournee, Simulation, SimulationInput, Curseurs).
 */

export const POLLING_MS = 5000;
export const STATUTS_EN_COURS = ['pending', 'processing'];
export const PERIODE_MAX_JOURS = 92;

export const FLUX_DEFAUT = [
    {flux: 'BIO', jours: 0},
    {flux: 'OM', jours: 1},
    {flux: 'CS', jours: 1},
    {flux: 'REC', jours: 1},
];

// Défauts de Curseurs.php (api) — affichés dans le wizard, jamais recalculés côté front.
export const CURSEURS_DEFAUT = {
    heuresParJour: 7,
    joursParSemaine: 5,
    miseEnPlaceMin: 2,
    tBacMin: 3.3,
    kSeul: 2,
    vitesseKmH: 40,
    vitesseTrajetKmH: 80,
    seuilBacsParHeure: 8,
    heureDebutJournee: '08:00',
};

// Lot 6 : plafond d'amplitude d'une journée, saisi en heures, envoyé en minutes (curseur `amplitudeMaxMin`, 420–900 côté API).
export const AMPLITUDE_DEFAUT_H = 12;
export const AMPLITUDE_MIN_H = 7;
export const AMPLITUDE_MAX_H = 15;

/** 12 → 720 ; 9,5 → 570. */
export const amplitudeEnMinutes = (h) => Math.round(Number(h) * 60);

/** 720 → 12 ; 570 → 9,5. */
export const amplitudeEnHeures = (min) => Number(min) / 60;

export const CLES_AVANCEES = ['miseEnPlaceMin', 'tBacMin', 'kSeul', 'vitesseKmH', 'vitesseTrajetKmH', 'seuilBacsParHeure', 'heureDebutJournee'];

/* ------------------------------------------------------------------ U-1 : tri et badge */

/** Rang de l'API : coût RH réel (0), sans coût (1), ancien modèle de coût `coutRH: false` (2, tout en fin de table). */
const rangScenario = (s) => (s.cout == null ? 1 : (s.coutRH === false ? 2 : 0));

/** Même ordre que l'API : rang, puis coût croissant ; à égalité, id croissant. */
export function trierScenarios(scenarios) {
    return [...(scenarios || [])].sort((a, b) => {
        const ra = rangScenario(a), rb = rangScenario(b);
        if (ra !== rb) return ra - rb;
        const na = a.cout == null, nb = b.cout == null;
        if (na !== nb) return na ? 1 : -1;
        if (!na && a.cout !== b.cout) return a.cout - b.cout;
        return a.id - b.id;
    });
}

/** Lot 6 (C-6) : `coutRH: false` = calculé avant le coût RH réel ; ligne grisée avec cette aide (l'API les range en fin de table). */
export const AIDE_ANCIEN_COUT = 'ancien modèle de coût, relancer le calcul';
export const ancienModeleCout = (s) => s?.coutRH === false;

/** Identifiant du scénario qui porte le badge « le moins cher » : la première ligne triée qui a un coût (jamais un ancien modèle de coût). */
export function idLeMoinsCher(scenarios) {
    const premier = trierScenarios(scenarios).find((s) => !ancienModeleCout(s));
    return premier && premier.cout != null ? premier.id : null;
}

/** Lignes de la table : triées, avec les drapeaux `moinsCher` et `ancienCout`. */
export function lignesScenarios(scenarios) {
    const id = idLeMoinsCher(scenarios);
    return trierScenarios(scenarios).map((s) => ({...s, moinsCher: s.id === id, ancienCout: ancienModeleCout(s)}));
}

/* ------------------------------------------------------------------ U-2 : en cours, polling */

export const estEnCours = (statut) => STATUTS_EN_COURS.includes(statut);

/** Lignes `enCours` de l'API : pending/processing grisées ; failed en rouge avec `erreur`. */
export function lignesEnCours(enCours) {
    return (enCours || []).map((s) => ({
        ...s,
        grise: estEnCours(s.statut),
        echec: s.statut === 'failed',
    }));
}

/** `pollingInterval` RTK Query : 5 s tant qu'un scénario est pending/processing, sinon 0 (arrêt). */
export function pollingScenarios(data) {
    return (data?.enCours || []).some((s) => estEnCours(s.statut)) ? POLLING_MS : 0;
}

/** Idem pour la page d'un scénario seul. */
export function pollingSimulation(simulation) {
    return simulation && estEnCours(simulation.statut) ? POLLING_MS : 0;
}

/* ------------------------------------------------------------------ U-3 : calendrier */

/**
 * État du calendrier d'après `GET /simulations/disponibilites` (contraint=true) : aucun PAV = aucun calendrier.
 * L'API n’a pas de route de comptage des lignes (écart signalé) : on ne sait donc ni la date d'import ni n.
 */
export function calendrierPresent(disponibilites) {
    return Array.isArray(disponibilites?.pavs) && disponibilites.pavs.length > 0;
}

/** Toggle « contraint à la collecte » : grisé et expliqué sans calendrier. */
export function toggleContraint(aCalendrier, erreur = false) {
    if (erreur) return {disabled: true, explication: 'Le calendrier de collecte n’a pas pu être vérifié : réessayez plus tard.'};
    return aCalendrier
        ? {disabled: false, explication: null}
        : {disabled: true, explication: 'Aucun calendrier de collecte pour ce patrimoine : importez un calendrier pour simuler une collecte contrainte.'};
}

/* ------------------------------------------------------------------ U-8 : base de départ */

const nombre = (v) => (v === '' || v == null || Number.isNaN(Number(v)) ? null : Number(v));

/** Une agence sans coordonnées est inéligible (explication affichée). Champs `latitude`/`longitude` : lot API 2b. */
export function eligibiliteAgence(agence) {
    const lat = nombre(agence?.latitude), lon = nombre(agence?.longitude);
    if (lat == null || lon == null) {
        return {eligible: false, raison: 'Cette agence n\'a pas de coordonnées : renseignez son adresse dans Administration > Agences, ou utilisez une adresse libre.'};
    }
    return {eligible: true, raison: null, lat, lon};
}

export function pointVide() {
    return {mode: 'agence', agence: null, adresse: '', lat: null, lon: null, libelle: ''};
}

/** `base` / `depot` du POST. Agence → `{agence: id}` ; adresse géocodée → `{lat, lon, libelle}` ; sinon null. */
export function pointPourPayload(p) {
    if (!p) return null;
    if (p.mode === 'agence') return p.agence != null && p.agence !== '' ? {agence: Number(p.agence)} : null;
    const lat = nombre(p.lat), lon = nombre(p.lon);
    if (lat == null || lon == null) return null;
    return {lat, lon, libelle: (p.libelle || p.adresse || '').trim()};
}

/** Résultat de `helper.geocodeAddress` ({latitude, longitude}) → point du formulaire en mode adresse. */
export function pointDepuisGeocodage(adresse, geo) {
    return {mode: 'adresse', agence: null, adresse, lat: geo.latitude, lon: geo.longitude, libelle: adresse};
}

/** Reprise des `parametres.base|depot` d'un scénario source : {agence} → agence ; {lat, lon[, libelle]} → adresse. */
export function pointDepuisParametres(p) {
    if (!p) return null;
    if (p.agence != null) return {...pointVide(), mode: 'agence', agence: Number(p.agence)};
    if (p.lat != null && p.lon != null) {
        return {mode: 'adresse', agence: null, adresse: p.libelle || '', lat: p.lat, lon: p.lon, libelle: p.libelle || ''};
    }
    return null;
}

/* ------------------------------------------------------------------ formulaire <-> payload */

export function valeursInitiales() {
    return {
        libelle: '',
        du: '',
        au: '',
        nbEquipes: 1,
        equipage: 'auto',
        base: pointVide(),
        depot: null, // null = aucun dépôt local
        contraint: false,
        deplacement: true, // l'équipe reste sur place (nuits hors base, forfait à partir de 3 nuits)
        tolerances: FLUX_DEFAUT.map((l) => ({...l})),
        heuresParJour: CURSEURS_DEFAUT.heuresParJour,
        joursParSemaine: CURSEURS_DEFAUT.joursParSemaine,
        amplitudeMaxH: AMPLITUDE_DEFAUT_H,
        horizonJournees: '',
        horizonVerrouille: false, // lot 5 : Oui = plafond ferme, les PAV en trop sont listés non affectés ; Non = le solveur ajoute des journées
        interdits: [],
        jours: [], // PAV figés : [{nomenclature, date}]
        miseEnPlaceMin: CURSEURS_DEFAUT.miseEnPlaceMin,
        tBacMin: CURSEURS_DEFAUT.tBacMin,
        kSeul: CURSEURS_DEFAUT.kSeul,
        vitesseKmH: CURSEURS_DEFAUT.vitesseKmH,
        vitesseTrajetKmH: CURSEURS_DEFAUT.vitesseTrajetKmH,
        seuilBacsParHeure: CURSEURS_DEFAUT.seuilBacsParHeure,
        heureDebutJournee: CURSEURS_DEFAUT.heureDebutJournee,
        coutHoraire: '',
        forfaitDeplacement: '',
        coutKm: '',
        calibre: null, // lot 5 : {journees, miseEnPlaceMin, tBacMin, kSeul} posé par « Appliquer » (calibrationRules), jamais envoyé
    };
}

const texte = (v) => (v == null ? '' : v);

/**
 * U-6 — wizard pré-rempli depuis un scénario source (`GET /simulations/{id}` : libelle, du, au, parametres).
 * `parametres.curseurs` est figé par l'API (coûts RH inclus) : on les reprend, modifiables.
 */
export function valeursDepuisSimulation(simulation) {
    const base = valeursInitiales();
    const p = simulation?.parametres || {};
    const c = p.curseurs || {};
    const tolerances = p.tolerances && Object.keys(p.tolerances).length > 0
        ? Object.entries(p.tolerances).map(([flux, jours]) => ({flux, jours}))
        : base.tolerances;
    const v = {
        ...base,
        libelle: `${simulation?.libelle || ''} (copie)`.trim(),
        du: simulation?.du || '',
        au: simulation?.au || '',
        nbEquipes: p.nbEquipes ?? base.nbEquipes,
        equipage: p.equipage ?? base.equipage,
        base: pointDepuisParametres(p.base) || base.base,
        depot: pointDepuisParametres(p.depot),
        contraint: p.contraint ?? base.contraint,
        deplacement: p.deplacement ?? true,
        tolerances,
        interdits: [...(p.verrous?.interdits || [])],
        jours: (p.verrous?.jours || []).map((j) => ({nomenclature: j.nomenclature, date: j.date})),
        horizonJournees: texte(c.horizonJournees),
        horizonVerrouille: p.verrous?.horizonVerrouille === true,
        coutHoraire: texte(c.coutHoraire),
        forfaitDeplacement: texte(c.forfaitDeplacement),
        coutKm: texte(c.coutKm),
    };
    for (const k of ['heuresParJour', 'joursParSemaine', ...CLES_AVANCEES]) {
        if (c[k] != null) v[k] = c[k];
    }
    if (c.amplitudeMaxMin != null) v.amplitudeMaxH = amplitudeEnHeures(c.amplitudeMaxMin);
    return v;
}

/** `?figer=COGCS248@2026-10-09` → {nomenclature, date} ou null. */
export function parseFiger(param) {
    const m = /^([^@\s]+)@(\d{4}-\d{2}-\d{2})$/.exec(param || '');
    return m ? {nomenclature: m[1], date: m[2]} : null;
}

export function paramFiger(nomenclature, date) {
    return `${nomenclature}@${date}`;
}

/** U-7 — ajoute le verrou {nomenclature, date} et suffixe le libellé « + verrou <PAV> » (sans doublon). */
export function figerJour(valeurs, {nomenclature, date}) {
    const existe = valeurs.jours.some((j) => j.nomenclature === nomenclature && j.date === date);
    if (existe) return valeurs;
    return {
        ...valeurs,
        libelle: `${valeurs.libelle.replace(/ \(copie\)$/, '')} + verrou ${nomenclature}`,
        jours: [...valeurs.jours, {nomenclature, date}],
    };
}

/** Valeurs d'ouverture du wizard selon l'URL : `?from=<source>` (+ `figer=`), sinon vierge. */
export function valeursOuverture(simulationSource, figer) {
    if (!simulationSource) return valeursInitiales();
    const v = valeursDepuisSimulation(simulationSource);
    return figer ? figerJour(v, figer) : v;
}

const ou = (v) => (v === '' || v == null ? undefined : Number(v));

/** U-4 : €/km obligatoire ; coût horaire et forfait facultatifs (l'API les fige depuis RHData s'ils sont absents). */
export function coutKmValide(v) {
    return v !== '' && v != null && Number.isFinite(Number(v)) && Number(v) >= 0;
}

export function peutLancer(valeurs) {
    return coutKmValide(valeurs.coutKm)
        && !!valeurs.libelle?.trim()
        && !!valeurs.du && !!valeurs.au
        && pointPourPayload(valeurs.base) != null;
}

/** Corps du POST /simulations. `patrimoine` : identifiant numérique ; `rejouerDe` : scénario importé à rejouer sur son snapshot. */
export function construirePayload(valeurs, patrimoine, rejouerDe = null) {
    const curseurs = {
        heuresParJour: Number(valeurs.heuresParJour),
        joursParSemaine: Number(valeurs.joursParSemaine),
        amplitudeMaxMin: amplitudeEnMinutes(valeurs.amplitudeMaxH ?? AMPLITUDE_DEFAUT_H),
        coutKm: Number(valeurs.coutKm),
    };
    for (const k of CLES_AVANCEES) {
        curseurs[k] = k === 'heureDebutJournee' ? String(valeurs[k]) : Number(valeurs[k]);
    }
    for (const k of ['horizonJournees', 'coutHoraire', 'forfaitDeplacement']) {
        const n = ou(valeurs[k]);
        if (n !== undefined) curseurs[k] = n;
    }
    const tolerances = {};
    for (const l of valeurs.tolerances) {
        const flux = String(l.flux || '').trim().toUpperCase();
        if (flux) tolerances[flux] = Number(l.jours);
    }
    const payload = {
        libelle: valeurs.libelle.trim(),
        patrimoine: Number(patrimoine),
        du: valeurs.du,
        au: valeurs.au,
        contraint: !!valeurs.contraint,
        deplacement: !!valeurs.deplacement,
        tolerances,
        curseurs,
        nbEquipes: Number(valeurs.nbEquipes),
        equipage: valeurs.equipage === 'auto' ? 'auto' : Number(valeurs.equipage),
        base: pointPourPayload(valeurs.base),
        verrous: {
            jours: valeurs.jours.map((j) => ({nomenclature: j.nomenclature, date: j.date})),
            interdits: [...valeurs.interdits],
            horizonVerrouille: valeurs.horizonVerrouille === true,
        },
    };
    if (rejouerDe != null && rejouerDe !== '') payload.rejouerDe = Number(rejouerDe);
    const depot = pointPourPayload(valeurs.depot);
    if (depot) payload.depot = depot;
    return payload;
}

/* ------------------------------------------------------------------ erreurs 422 */

/**
 * `violations[].propertyPath` de l'API → erreurs Formik IMBRIQUÉES (`setIn`) + violations non rattachées.
 * Les curseurs sont à plat dans le formulaire (`curseurs.coutKm` → `coutKm`), les tolérances sont des lignes
 * (`tolerances.OM` → `tolerances[1].jours`), les verrous deviennent `jours[i].*` / `interdits[i]`.
 * Un chemin dont la racine n’a pas de champ affiché (ex. `contraint`, `verrous.pavsInterdits`) ou sans chemin
 * va dans `globales` (liste de messages, alerte visible sur toutes les étapes).
 * @returns {{erreurs: object, globales: string[]}}
 */
const RACINES_AFFICHEES = ['libelle', 'du', 'au', 'nbEquipes', 'equipage', 'base', 'depot', 'tolerances', 'interdits', 'jours',
    'heuresParJour', 'joursParSemaine', 'amplitudeMaxH', 'horizonJournees', 'horizonVerrouille', 'coutHoraire', 'forfaitDeplacement', 'coutKm', ...CLES_AVANCEES];

export function violationsVersChamps(violations, valeurs) {
    let erreurs = {};
    const globales = [];
    for (const v of violations || []) {
        const chemin = v.propertyPath || '';
        let cible = null;
        if (chemin.startsWith('curseurs.')) {
            cible = chemin.slice('curseurs.'.length);
            if (cible === 'amplitudeMaxMin') cible = 'amplitudeMaxH';
        } else if (chemin.startsWith('tolerances.')) {
            const flux = chemin.slice('tolerances.'.length).toUpperCase();
            const i = (valeurs?.tolerances || []).findIndex((l) => String(l.flux).toUpperCase() === flux);
            cible = i >= 0 ? `tolerances.${i}.jours` : null;
        } else if (chemin.startsWith('verrous.')) {
            cible = chemin.slice('verrous.'.length);
        } else if (chemin) {
            cible = chemin;
        }
        const racine = cible ? cible.split('.')[0] : null;
        if (!cible || !RACINES_AFFICHEES.includes(racine)) {
            globales.push(v.message);
        } else if (getIn(erreurs, cible) === undefined) {
            erreurs = setIn(erreurs, cible, v.message);
        }
    }
    return {erreurs, globales};
}

/**
 * Soumission d'un scénario, commune au wizard V1 et à l'atelier : POST via `creer(payload)` puis, sur une 422 de validation,
 * erreurs de champs imbriquées + violations globales. Toute autre erreur est relancée (toast côté composant).
 * @returns {Promise<{cree: object}|{erreurs: object, globales: string[]}>}
 */
export async function soumettreScenario(creer, valeurs, patrimoine, construire = construirePayload) {
    try {
        return {cree: await creer(construire(valeurs, patrimoine))};
    } catch (e) {
        if (e?.status === 422 && Array.isArray(e.data?.violations)) return violationsVersChamps(e.data.violations, valeurs);
        throw e;
    }
}

/** Racines (étapes) des erreurs imbriquées, pour revenir sur la première étape fautive. */
export function etapeMinimale(erreurs) {
    const etapes = Object.keys(erreurs || {}).map(etapeDuChamp);
    return etapes.length ? Math.min(...etapes) : null;
}

/** Sans calendrier, « contraint » est forcé à false (y compris en duplication). */
export function appliquerCalendrier(valeurs, aCalendrier) {
    return {...valeurs, contraint: aCalendrier ? !!valeurs.contraint : false};
}

/** Étape du wizard (0, 1, 2) qui porte un chemin Formik, pour revenir sur la première erreur. */
export function etapeDuChamp(chemin) {
    const racine = String(chemin).split('.')[0];
    if (['libelle', 'du', 'au', 'nbEquipes', 'equipage', 'base', 'depot'].includes(racine)) return 0;
    if (['coutHoraire', 'forfaitDeplacement', 'coutKm'].includes(racine)) return 2;
    return 1;
}

/* ------------------------------------------------------------------ U-5 : journée, carte */

/**
 * Marqueurs numérotés d'une équipe, dans l'ordre de l'API (`ordre`) ; le numéro affiché est l'`ordre` de la visite,
 * identique à celui de la liste. Un PAV sans coordonnées n’a pas de point (l'API l'omet) : pas de marqueur.
 */
export function marqueursEquipe(points, equipe) {
    return (points || [])
        .filter((p) => p.equipe === equipe)
        .sort((a, b) => a.ordre - b.ordre)
        .map((p) => ({numero: p.ordre, nomenclature: p.nomenclature, position: [p.lat, p.lon]}));
}

/** Une journée porte un trajet base/dépôt ↔ secteur si `libelleTrajet` est non nul ou `minutesTrajets` > 0. */
export function journeeATrajet(j) {
    return (j?.libelleTrajet != null && j.libelleTrajet !== '') || Number(j?.minutesTrajets) > 0;
}

/** Tracé (segments base/dépôt ↔ visites seulement si `avecTrajet`) : dépôt s'il existe, sinon base → visites dans l'ordre → retour au même point. Positions [lat, lon]. */
export function traceEquipe(marqueurs, base, depot, avecTrajet = true) {
    if (marqueurs.length === 0) return [];
    if (!avecTrajet) return marqueurs.map((m) => m.position);
    const origine = [depot, base].find((p) => p && p.lat != null && p.lon != null);
    const b = origine ? [[origine.lat, origine.lon]] : [];
    return [...b, ...marqueurs.map((m) => m.position), ...b];
}

/** Visites d'une journée d'équipe, dans l'ordre de l'API (`ordre` croissant). */
export function visitesOrdonnees(journee) {
    return [...(journee?.visites || [])].sort((a, b) => a.ordre - b.ordre);
}

/** Raisons générales du scénario : sans les lignes « <date> équipe N : … » (déjà dans la table des journées). */
export function raisonsGenerales(raisons) {
    return (raisons || []).filter((r) => !/^\d{4}-\d{2}-\d{2} équipe \d+ : /.test(r));
}

/**
 * Décision sur la requête `GET /simulations/calendriers` : 'repli' sur disponibilites uniquement si 404 (endpoint absent),
 * 'erreur' pour toute autre erreur, 'chargement' tant qu'il n'y a pas de donnée COURANTE (autre patrimoine, requête en vol), sinon 'ok'.
 */
export function decisionCalendrier({isError, status, currentData, isFetching}) {
    if (isError) return status === 404 ? {mode: 'repli'} : {mode: 'erreur'};
    if (currentData === undefined || (isFetching && currentData === undefined)) return {mode: 'chargement'};
    return {mode: 'ok', details: currentData};
}

/** Positions du cadrage de la carte : les visites seulement (base et dépôt sont dessinés mais hors cadrage). */
export function positionsCadrage(points, equipes) {
    return (equipes || []).flatMap((e) => marqueursEquipe(points, e).map((m) => m.position));
}

/** Bandeau calendrier depuis `GET /simulations/calendriers` : « Calendrier présent : N lignes, M conteneurs couverts sur P ». */
export function texteCalendrier(d) {
    if (!d || d.lignes == null) return "Calendrier de collecte présent pour ce patrimoine.";
    return `Calendrier présent : ${d.lignes} lignes, ${d.conteneursCouverts} conteneurs couverts sur ${d.conteneursPatrimoine}`;
}

/* ------------------------------------------------------------------ formats */

export function formatEuros(n) {
    if (n == null) return '—';
    return `${Math.round(n).toLocaleString('fr-FR')} €`;
}

export function formatHeuresDepuisMinutes(min) {
    if (min == null) return '—';
    const total = Math.round(min);
    const h = Math.floor(total / 60), m = total % 60;
    return `${h} h ${String(m).padStart(2, '0')}`;
}

export function formatHeures(h) {
    if (h == null) return '—';
    return `${Number(h).toLocaleString('fr-FR', {maximumFractionDigits: 1})} h`;
}

export function formatKm(km) {
    if (km == null) return '—';
    return `${Number(km).toLocaleString('fr-FR', {maximumFractionDigits: 0})} km`;
}

export function libelleEquipage(e) {
    if (e === 1 || e === '1') return 'seul';
    if (e === 2 || e === '2') return 'binôme';
    return 'auto';
}

/** « 1 équipe · binôme » */
export function resumeEquipes(s) {
    return `${s.nbEquipes} équipe${s.nbEquipes > 1 ? 's' : ''} · ${libelleEquipage(s.equipage)}`;
}

/** « OM 1 · CS 1 » ou « libre » */
export function resumeContraint(s) {
    if (!s.contraint) return 'libre';
    const t = Object.entries(s.tolerances || {}).map(([f, j]) => `${f} ${j}`).join(' · ');
    return t ? `contraint (${t})` : 'contraint';
}
